/**
 * SplitOrderIssuesList - Tab content for the Split Orders issues tab
 *
 * Shows orders that are split across multiple fulfillment locations.
 * These orders are held from the production queue until resolved. "Resolve"
 * opens the BLU-563 resolver assignment board (OrderResolutionFlow), which
 * moves the Shopify fulfilment orders + repartitions production in one apply;
 * the kebab keeps a pure-bookkeeping "Mark resolved" for issues that were
 * already fixed outside the app.
 *
 * BLU-563 R3 quick resolutions (rendered only when the row carries BOTH
 * prod_order_id AND the linked ERP order_id):
 * - "Accept split": AlertDialog confirm -> acceptSplitOrder({ order_id }) —
 *   keeps every unit where Shopify has it (zero moves) and releases each half.
 * - "Consolidate to…": Popover that lazily ranks full-coverage locations via
 *   getSplitConsolidationOptions, then inline-confirms consolidateOrderTo.
 * Both mirror the resolver's honest toast contract (OrderResolutionFlow):
 * outcome-named success, persistent warning on unmoved committed stock, and
 * persistent error on partial apply.
 */
import { useState, useMemo, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle,
  Check,
  ExternalLink,
  GitBranch,
  GitMerge,
  Loader2,
  MapPin,
  MoreHorizontal,
  PackageX,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { trpc, type RouterOutputs } from '@/lib/trpc';
import { getUserMessage } from '@/lib/trpc-error-utils';
import { getShopifyAdminOrderUrl } from '@/lib/shopify-urls';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useSplitOrderIssues, useResolveIssue } from '../hooks/useProductionIssues';
import { IssueSearchInput } from './IssueSearchInput';
import type { ProductionIssue } from '../types';

const PAGE_SIZE = 25;

/** Which per-card action is currently in flight (extends the old resolvingIssueId). */
type QuickAction = 'markResolved' | 'accept' | 'consolidate';

/** The shared resolver-saga result (acceptSplitOrder and consolidateOrderTo return it). */
type QuickResolutionResult = RouterOutputs['productionOrders']['issues']['acceptSplitOrder'];

type ConsolidationOption =
  RouterOutputs['productionOrders']['issues']['getSplitConsolidationOptions']['options'][number];

interface SplitOrderIssuesListProps {
  locationId: string;
  /**
   * Optional action rendered right-aligned in the tab heading row (e.g. the
   * split-order settings gear from IssuesPage) — keeps the action in normal
   * flow instead of absolutely positioned over the list.
   */
  headerAction?: ReactNode;
}

export function SplitOrderIssuesList({ locationId, headerAction }: SplitOrderIssuesListProps) {
  const { t } = useTranslation('production');
  const utils = trpc.useUtils();
  const [showResolved, setShowResolved] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const { data: issues, isLoading } = useSplitOrderIssues(locationId, {
    status: showResolved ? undefined : 'OPEN',
  });

  const resolveMutation = useResolveIssue();
  const acceptMutation = trpc.productionOrders.issues.acceptSplitOrder.useMutation();
  const consolidateMutation = trpc.productionOrders.issues.consolidateOrderTo.useMutation();

  // Track WHICH card (and which action) is in flight so only that card's
  // actions disable — a shared isPending would freeze every card in the list.
  const [pendingAction, setPendingAction] = useState<{
    issueId: string;
    action: QuickAction;
  } | null>(null);

  const handleResolve = async (issueId: string) => {
    setPendingAction({ issueId, action: 'markResolved' });
    try {
      await resolveMutation.mutateAsync({
        issue_id: issueId,
        resolution_method: 'manual',
      });
      toast.success(t('issues.splitOrders.resolved'));
    } catch (error) {
      toast.error(getUserMessage(error));
    } finally {
      setPendingAction(null);
    }
  };

  // Honest toast contract — mirrors OrderResolutionFlow's onSuccess exactly:
  // unmoved committed stock / a committed error is a persistent warning (never
  // a fake green); otherwise the success names the outcome + resolved count.
  const notifyResolutionOutcome = (data: QuickResolutionResult, locationName?: string) => {
    utils.productionOrders.issues.invalidate();
    utils.productionOrders.getOrderQueue.invalidate();
    utils.productionOrders.getQueueCounts.invalidate();

    const resolvedCount = data.resolved_issue_count ?? 0;
    const hadFollowup =
      (data.unmoved?.length ?? 0) > 0 ||
      !!(data as { committed_error?: string }).committed_error;

    if (hadFollowup) {
      const firstUnmoved = data.unmoved?.[0];
      toast.warning(t('issues.resolveFlow.partialTitle'), {
        description: firstUnmoved
          ? t('issues.resolveFlow.partialDescUnmoved', {
              sku: firstUnmoved.sku,
              count: data.unmoved?.length ?? 0,
            })
          : t('issues.resolveFlow.partialDesc'),
        duration: Infinity,
      });
    } else if (data.outcome === 'consolidated') {
      toast.success(
        t('issues.resolveFlow.successConsolidated', {
          location: locationName || t('issues.resolveFlow.oneLocation'),
        }),
        resolvedCount > 0
          ? { description: t('issues.resolveFlow.resolvedIssues', { count: resolvedCount }) }
          : undefined,
      );
    } else {
      toast.success(
        t('issues.resolveFlow.successSplit', {
          count: data.resulting_location_ids?.length ?? 0,
        }),
        resolvedCount > 0
          ? { description: t('issues.resolveFlow.resolvedIssues', { count: resolvedCount }) }
          : undefined,
      );
    }
  };

  // Mirrors OrderResolutionFlow's onError: validation stays a plain error; a
  // partial apply / verify failure is persistent — the server left the order
  // safely HELD and its message explains the retry semantics.
  const notifyResolutionError = (error: unknown) => {
    const code = (error as { data?: { code?: string } | null })?.data?.code;
    if (code === 'BAD_REQUEST') {
      toast.error(getUserMessage(error));
    } else {
      toast.error(t('issues.resolveFlow.failureTitle'), {
        description: getUserMessage(error),
        duration: Infinity,
      });
    }
  };

  const handleAcceptSplit = async (issue: ProductionIssue) => {
    if (!issue.order_id) return;
    setPendingAction({ issueId: issue.id, action: 'accept' });
    try {
      const data = await acceptMutation.mutateAsync({ order_id: issue.order_id });
      notifyResolutionOutcome(data);
    } catch (error) {
      notifyResolutionError(error);
    } finally {
      setPendingAction(null);
    }
  };

  const handleConsolidate = async (issue: ProductionIssue, option: ConsolidationOption) => {
    if (!issue.order_id) return;
    setPendingAction({ issueId: issue.id, action: 'consolidate' });
    try {
      const data = await consolidateMutation.mutateAsync({
        order_id: issue.order_id,
        target_location_id: option.location_id,
      });
      notifyResolutionOutcome(data, option.location_name ?? undefined);
    } catch (error) {
      notifyResolutionError(error);
    } finally {
      setPendingAction(null);
    }
  };

  // Filter (client-side) then paginate
  const isSearching = search.trim().length > 0;
  const allIssues = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = issues ?? [];
    if (!q) return list;
    return list.filter((i) => {
      const name = i.order_name ?? (i.metadata?.order_name as string) ?? '';
      return `${name} ${i.customer_display ?? ''}`.toLowerCase().includes(q);
    });
  }, [issues, search]);
  const totalItems = allIssues.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paginatedIssues = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return allIssues.slice(start, start + PAGE_SIZE);
  }, [allIssues, safePage]);

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-20 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  return (
    <div>
      {/* Tab heading (headerAction sits right-aligned in the heading row). */}
      <div className="mb-4 flex items-start justify-between gap-2">
        <div className="space-y-0.5">
          <h2 className="text-sm font-semibold">{t('issues.splitOrders.listHeading')}</h2>
          <p className="max-w-prose text-sm text-muted-foreground">
            {t('issues.splitOrders.listDesc')}
          </p>
        </div>
        {headerAction && <div className="shrink-0">{headerAction}</div>}
      </div>
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <IssueSearchInput
          value={search}
          onChange={(v) => {
            setSearch(v);
            setPage(1);
          }}
          placeholder={t('issues.filters.searchPlaceholder')}
          ariaLabel={t('issues.filters.searchAriaLabel')}
          clearLabel={t('issues.filters.clearSearch')}
        />
        <div className="flex items-center gap-2">
          <Switch
            id="show-resolved-split"
            checked={showResolved}
            onCheckedChange={(checked) => {
              setShowResolved(checked);
              setPage(1);
            }}
          />
          <Label htmlFor="show-resolved-split" className="text-sm text-muted-foreground">
            {t('issues.splitOrders.showResolved')}
          </Label>
        </div>
        <p className="text-sm text-muted-foreground ml-auto">
          {isSearching
            ? t('issues.searchMatchCount', { count: totalItems })
            : t('issues.splitOrders.issueCount', { count: totalItems })}
        </p>
      </div>

      {/* Issue cards */}
      {paginatedIssues.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <GitBranch className="h-12 w-12 text-muted-foreground" aria-hidden="true" />
            <p className="mt-4 text-lg font-medium">
              {isSearching ? t('issues.splitOrders.noResults') : t('issues.splitOrders.noIssues')}
            </p>
            {!isSearching && (
              <p className="text-sm text-muted-foreground">
                {showResolved
                  ? t('issues.splitOrders.noneRecorded')
                  : t('issues.splitOrders.noCurrentSplits')}
              </p>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {paginatedIssues.map((issue) => (
            <SplitOrderCard
              key={issue.id}
              issue={issue}
              onResolve={handleResolve}
              onAcceptSplit={handleAcceptSplit}
              onConsolidate={handleConsolidate}
              pendingAction={pendingAction?.issueId === issue.id ? pendingAction.action : null}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          <Button
            variant="outline"
            size="sm"
            disabled={safePage <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            {t('issues.pagination.previous')}
          </Button>
          <span className="text-sm text-muted-foreground">
            {t('issues.pagination.pageOf', { current: safePage, total: totalPages })}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={safePage >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            {t('issues.pagination.next')}
          </Button>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// SplitOrderCard
// ============================================================================

interface SplitOrderCardProps {
  issue: ProductionIssue;
  onResolve: (issueId: string) => void;
  onAcceptSplit: (issue: ProductionIssue) => void;
  onConsolidate: (issue: ProductionIssue, option: ConsolidationOption) => void;
  /** Which of THIS card's actions is in flight (null = idle). */
  pendingAction: QuickAction | null;
}

function SplitOrderCard({
  issue,
  onResolve,
  onAcceptSplit,
  onConsolidate,
  pendingAction,
}: SplitOrderCardProps) {
  const { t } = useTranslation('production');
  const navigate = useNavigate();
  const { organization } = useOrganization();
  const [acceptConfirmOpen, setAcceptConfirmOpen] = useState(false);
  const metadata = issue.metadata;
  const allLocations = (metadata?.all_locations as Array<{ location_name: string }>) ?? [];
  const locationCount = (metadata?.location_count as number) ?? allLocations.length;
  const shopifyOrderId = metadata?.shopify_order_id as number | undefined;
  const isResolved = issue.status === 'RESOLVED';
  const isBusy = pendingAction !== null;
  const orderLabel = issue.order_name ?? (metadata?.order_name as string) ?? 'Unknown Order';
  // Quick actions need the linked ERP orders.id (NOT prod_order_id) — hide
  // them when either id is missing; "Resolve…" stays the full-control path.
  const canQuickResolve = !!issue.prod_order_id && !!issue.order_id;

  const locationNames = allLocations
    .map((l) => l.location_name)
    .filter(Boolean)
    .join(', ');

  // Use the validated admin-URL builder (the old hand-built `/orders/{id}` form
  // omitted `/store/{slug}` and 404'd). Renders no link when inputs are invalid.
  const shopifyUrl = getShopifyAdminOrderUrl(organization?.shopify_domain, shopifyOrderId);

  return (
    <Card className={isResolved ? 'opacity-60' : undefined}>
      <CardContent className="py-4">
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div className="flex-shrink-0 rounded-md p-1.5 text-purple-600 bg-purple-50">
            <GitBranch className="h-4 w-4" aria-hidden="true" />
          </div>

          {/* Main content */}
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  {/* Order number is the Shopify hyperlink (no separate "View in
                      Shopify" button on this surface). Plain text when the admin
                      URL can't be built. */}
                  {shopifyUrl ? (
                    <a
                      href={shopifyUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={t('issues.splitOrders.viewOnShopifyAria', { order: orderLabel })}
                      className="inline-flex items-center gap-1 rounded text-sm font-semibold text-threadheads-purple hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                    >
                      {orderLabel}
                      <ExternalLink className="h-3 w-3" aria-hidden="true" />
                    </a>
                  ) : (
                    <span className="font-semibold text-sm">{orderLabel}</span>
                  )}
                  {issue.customer_display && (
                    <span className="text-sm text-muted-foreground">
                      {issue.customer_display}
                    </span>
                  )}
                  <Badge variant="outline" className="text-purple-600 bg-purple-50 text-xs">
                    {t('issues.splitOrders.badge')}
                  </Badge>
                  {isResolved && (
                    <Badge variant="outline" className="text-xs text-green-600 border-green-200 bg-green-50">
                      {t('issues.splitOrders.resolvedBadge')}
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {t('issues.splitOrders.splitAcross', { count: locationCount })}
                  {locationNames && `: ${locationNames}`}
                </p>
              </div>
              <span className="text-xs text-muted-foreground whitespace-nowrap shrink-0">
                {formatDistanceToNow(new Date(issue.created_at), { addSuffix: true })}
              </span>
            </div>

            {/* Actions: "Resolve…" opens the assignment board (needs the
                prod_order id the issue rows carry) and stays the primary
                full-control path; "Accept split" / "Consolidate to…" are the
                BLU-563 R3 quick resolutions (need the ERP order_id too); the
                kebab keeps the pure bookkeeping "Mark resolved" for splits
                fixed outside the app. Without a prod_order id there is no
                board to open, so "Mark resolved" IS the primary action — a
                visible outline button, not hidden in a kebab. */}
            {!isResolved && (
              <div className="flex flex-wrap items-center gap-2 mt-2">
                {issue.prod_order_id ? (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1.5"
                      disabled={isBusy}
                      onClick={() =>
                        navigate(`/production/issues/resolve/${issue.prod_order_id}`)
                      }
                    >
                      <GitBranch className="h-3.5 w-3.5" aria-hidden="true" />
                      {t('issues.splitOrders.resolveAction')}
                    </Button>
                    {canQuickResolve && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-1.5"
                          disabled={isBusy}
                          onClick={() => setAcceptConfirmOpen(true)}
                        >
                          {pendingAction === 'accept' ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                          ) : (
                            <Check className="h-3.5 w-3.5" aria-hidden="true" />
                          )}
                          {t('issues.splitOrders.acceptAction')}
                        </Button>
                        <ConsolidatePopover
                          issue={issue}
                          orderLabel={orderLabel}
                          disabled={isBusy}
                          isPending={pendingAction === 'consolidate'}
                          onConsolidate={onConsolidate}
                        />
                      </>
                    )}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          aria-label={t('issues.splitOrders.moreActions')}
                          disabled={isBusy}
                        >
                          <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start">
                        <DropdownMenuItem
                          onClick={() => onResolve(issue.id)}
                          disabled={isBusy}
                        >
                          <Check className="mr-2 h-4 w-4" aria-hidden="true" />
                          {t('issues.splitOrders.markResolved')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                    onClick={() => onResolve(issue.id)}
                    disabled={isBusy}
                  >
                    <Check className="h-3.5 w-3.5" aria-hidden="true" />
                    {t('issues.splitOrders.markResolved')}
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </CardContent>

      {/* Accept-split confirm: keep the order split exactly as it is today. */}
      <AlertDialog open={acceptConfirmOpen} onOpenChange={setAcceptConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('issues.splitOrders.acceptConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('issues.splitOrders.acceptConfirmBody')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('issues.resolveFlow.confirmCancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="gap-1.5 bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
              onClick={() => {
                setAcceptConfirmOpen(false);
                onAcceptSplit(issue);
              }}
            >
              <Check className="h-4 w-4" aria-hidden="true" />
              {t('issues.splitOrders.acceptConfirmAction')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

// ============================================================================
// ConsolidatePopover — lazy-ranked full-coverage targets + inline confirm.
// Mirrors OrderDetailModal's routing_suggestions rows (location name +
// availability badge) in a compact popover. Fail-closed: data_complete=false
// means "can't determine", so the popover points at Resolve instead.
// ============================================================================

function ConsolidatePopover({
  issue,
  orderLabel,
  disabled,
  isPending,
  onConsolidate,
}: {
  issue: ProductionIssue;
  orderLabel: string;
  disabled: boolean;
  isPending: boolean;
  onConsolidate: (issue: ProductionIssue, option: ConsolidationOption) => void;
}) {
  const { t } = useTranslation('production');
  const [open, setOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState<ConsolidationOption | null>(null);

  // Lazy: only fetched while the popover is open.
  const optionsQuery = trpc.productionOrders.issues.getSplitConsolidationOptions.useQuery(
    { prod_order_id: issue.prod_order_id ?? '' },
    { enabled: open && !!issue.prod_order_id }
  );
  const data = optionsQuery.data;

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="gap-1.5" disabled={disabled}>
            {isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            ) : (
              <GitMerge className="h-3.5 w-3.5" aria-hidden="true" />
            )}
            {t('issues.splitOrders.consolidateAction')}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-0" align="start">
          <p className="border-b px-3 py-2 text-xs font-medium text-muted-foreground">
            {t('issues.splitOrders.consolidateHeading')}
          </p>
          {optionsQuery.isLoading ? (
            <div className="space-y-2 p-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-9 w-full" />
              ))}
            </div>
          ) : optionsQuery.isError ? (
            <div className="flex gap-2 px-3 py-3 text-sm text-muted-foreground">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span>{t('issues.splitOrders.consolidateLoadError')}</span>
            </div>
          ) : !data || !data.data_complete ? (
            // Fail-closed: incomplete stock data means we cannot rank targets.
            <div className="flex gap-2 bg-amber-50 px-3 py-3 text-sm text-amber-900">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span>{t('issues.splitOrders.consolidateIncomplete')}</span>
            </div>
          ) : data.options.length === 0 ? (
            <div className="flex gap-2 px-3 py-3 text-sm text-muted-foreground">
              <PackageX className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span>{t('issues.splitOrders.consolidateNoOptions')}</span>
            </div>
          ) : (
            <ul className="max-h-64 overflow-y-auto py-1">
              {data.options.map((option) => (
                <li key={option.location_id}>
                  <button
                    type="button"
                    className="flex min-h-11 w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-muted/50 focus-visible:bg-muted/50 focus-visible:outline-none"
                    onClick={() => {
                      setOpen(false);
                      setConfirmTarget(option);
                    }}
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      <MapPin className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                      <span className="truncate font-medium">
                        {option.location_name ?? t('issues.splitOrders.unnamedLocation')}
                      </span>
                    </span>
                    <span className="shrink-0 rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium tabular-nums text-green-700">
                      {t('issues.splitOrders.consolidateAvailable', {
                        count: option.total_available,
                      })}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </PopoverContent>
      </Popover>

      {/* Inline confirm for the picked target. */}
      <AlertDialog
        open={confirmTarget !== null}
        onOpenChange={(next) => {
          if (!next) setConfirmTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('issues.splitOrders.consolidateConfirmTitle', {
                order: orderLabel,
                location:
                  confirmTarget?.location_name ?? t('issues.splitOrders.unnamedLocation'),
              })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('issues.splitOrders.consolidateConfirmBody')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('issues.resolveFlow.confirmCancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="gap-1.5 bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
              onClick={() => {
                const target = confirmTarget;
                setConfirmTarget(null);
                if (target) onConsolidate(issue, target);
              }}
            >
              <GitMerge className="h-4 w-4" aria-hidden="true" />
              {t('issues.splitOrders.consolidateConfirmAction')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
